// ============================================================
// BillWordingPanel.tsx — B3: Admin › Bill wording (ADMINS ONLY — the page
// and the API). The firm's legal details, the structured complaints contact,
// and the three bill notices ("Your rights", complaints, SRA footer). Blank
// notice = Casevo's default text. The details are filled into the notices
// wherever they're inserted (backend lib/billWording).
//   • Status: "DRAFT: not yet approved by a solicitor" vs "Approved by [name]
//     on [date]". ANY save makes it DRAFT again (the server resets it).
//   • "Approve wording" — refused while anything is missing (listed).
//   • Until approved, bills preview with "DRAFT WORDING: NOT FOR SENDING" and
//     can't be issued or sent.
// B3b (M9) — no raw {tokens} on this page: (B3c) each notice has ONE
// "Insert ▾" menu listing the details by name, and an inserted detail
// shows in the text as [Complaints contact]. It's stored as the server's
// token ({complaintsContact}) — wordingToDisplay / wordingToTokens convert,
// so the saved templates are unchanged. The firm's legal structure decides
// whether a company registration is needed (a partnership / sole
// practitioner has none).
// (Batch 2) "Withdraw approval" asks first (ConfirmDialogue); unsaved edits
// are guarded — leaving / navigating away asks first (M16).
// (Big-audit fix batch 1) "Firm names" — a read-first card showing BOTH
// names and where each is printed, so a firm can see why its documents show
// two: the TRADING name (Firm.name — letterheads, signing pages, e-signature
// emails, the completion certificate; not editable here) and the LEGAL name
// (Firm.legalName — the SRA footer and the signature line). The legal name
// is edited from its pencil (Cancel / Save in the card's footer; a save makes
// the wording DRAFT again, like any other change here).
// (Big-audit fix batch 2) The same pencil edits the TRADING name too
// (PATCH /api/firm/name — admins only, audited, live): required, trimmed, at
// most FIRM_NAME_MAX. The edit view says it changes NEW documents only. The
// signed-in user's firm name (app shell) updates at once; colleagues' on
// the live firm "settings" message (hooks/useLiveTopics).
// ============================================================
import ActionBox, { ErrorNote } from '../shared/ActionBox'
import React, { useEffect, useRef, useState } from 'react'
import { MENU_SURFACE, MenuItem } from '../shared/menuUi'
import { SettingsCard, SettingsRow, RowSmall, S_BTN_PRIMARY, S_BTN_GHOST, S_BTN_GHOST_SM, S_LABEL, S_NOTE, S_FIELDS, S_FORMFOOT, S_FIELD_ERROR, sInput, SettingsSaved } from './settingsUi'
import { EditFooter, EditPencil } from '../settlement/settlementUi'
import { approveBillWordingApi, getBillWordingApi, patchBillWordingApi, patchFirmNameApi, unapproveBillWordingApi } from '../../api/casevoApi'
import FieldError from '../shared/FieldError'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useFirmChange } from '../../utils/liveEvents'
import type { BillWordingPatch, BillWordingSettings, LegalStructure } from '../../types/billing.types'
import { serverErrorMessage } from '../../utils/serverError'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'

type TextKey = 'legalName' | 'companyRegistration' | 'complaintsContactName' | 'complaintsContactRole' | 'complaintsEmail' | 'complaintsPhone' | 'complaintsProcedureUrl'
type NoticeKey = 'billRightsWording' | 'billComplaintsWording' | 'billSraFooter'
export const COMPANY_REGISTRATION_HINT = 'Company number, where registered, and registered office address'
// (Big-audit fix batch 1) Where each of the firm's names is printed (backend:
// Firm.name → letterheads / signing / e-sign emails / certificate; Firm.legalName
// → lib/billWording DEFAULT_SRA_FOOTER {legalName} + signatureLine).
export const TRADING_NAME_WHERE = 'Shown at the top of bills and settlement statements, on signing pages, in e-signature emails and on the completion certificate.'
// (Big-audit fix batch 2) The trading name: limits (the server checks the same) and what a change affects.
export const FIRM_NAME_MAX = 120
export const TRADING_NAME_CHANGE_NOTE = 'Changes the name on letters, signing pages and the e-signature sender name for NEW documents only. Documents already issued or sealed never change.'
export const tradingNameError = (v: string): string | null => {
  const t = v.replace(/\s+/g, ' ').trim()
  if (!t) return 'Enter the firm’s trading name.'
  if (t.length > FIRM_NAME_MAX) return `The trading name must be ${FIRM_NAME_MAX} characters or fewer.`
  if (/[<>]/.test(t)) return 'The trading name can’t contain < or >.'
  return null
}
export const LEGAL_NAME_WHERE = 'Shown in the SRA footer and the signature line (“authorised to sign on behalf of …”) on bills and settlement statements.'
export const namesDiffer = (trading: string | null | undefined, legal: string | null | undefined): boolean => {
  const a = (trading ?? '').trim().toLowerCase()
  const b = (legal ?? '').trim().toLowerCase()
  return !!a && !!b && a !== b
}
const DETAILS: { key: TextKey; label: string; placeholder: string; type?: string }[] = [
  { key: 'companyRegistration', label: 'Company registration', placeholder: 'e.g. Registered in England and Wales, no. OC123456. Registered office: …' },
]
const COMPLAINTS: { key: TextKey; label: string; placeholder: string; type?: string }[] = [
  { key: 'complaintsContactName', label: 'Name', placeholder: 'e.g. Jane Smith' },
  { key: 'complaintsContactRole', label: 'Role', placeholder: 'e.g. Complaints Partner' },
  { key: 'complaintsEmail', label: 'Email', placeholder: 'complaints@yourfirm.co.uk', type: 'email' },
  { key: 'complaintsPhone', label: 'Phone', placeholder: '0113 000 0000', type: 'tel' },
  { key: 'complaintsProcedureUrl', label: 'Complaints procedure web address', placeholder: 'www.yourfirm.co.uk/complaints' },
]
const NOTICES: { key: NoticeKey; label: string; sub: string; preview: 'rights' | 'complaints' | 'sraFooter' }[] = [
  { key: 'billRightsWording', label: 'Your rights (Solicitors Act 1974, Part III)', sub: 'Printed on every statute bill.', preview: 'rights' },
  { key: 'billComplaintsWording', label: 'Complaints', sub: 'Printed on every bill. Must include the Legal Ombudsman details.', preview: 'complaints' },
  { key: 'billSraFooter', label: 'SRA footer', sub: 'Printed at the foot of every bill. With no VAT number, the VAT sentence is left out.', preview: 'sraFooter' },
]
const longDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '')
export const wordingStatusText = (w: Pick<BillWordingSettings, 'approved' | 'approvedBy' | 'approvedAt'>): string =>
  w.approved ? `Approved by ${w.approvedBy ?? 'a colleague'} on ${longDate(w.approvedAt)}` : 'DRAFT: not yet approved by a solicitor'

// ── {token} ⇄ [Label] (pure; exported for tests) ──
type Placeholder = { key: string; label: string }
const escRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
export const wordingToDisplay = (text: string, placeholders: Placeholder[]): string =>
  placeholders.reduce((t, p) => t.split(`{${p.key}}`).join(`[${p.label}]`), text)
export const wordingToTokens = (text: string, placeholders: Placeholder[]): string =>
  placeholders.reduce((t, p) => t.replace(new RegExp(escRe(`[${p.label}]`), 'g'), `{${p.key}}`), text)
// Put `insert` at the textarea's caret (else the end); returns the new text + caret.
export const insertAt = (text: string, insert: string, start: number | null, end: number | null): { text: string; caret: number } => {
  const a = start ?? text.length
  const b = end ?? a
  const before = text.slice(0, a)
  const pad = before && !/\s$/.test(before) ? ' ' : ''
  const next = `${before}${pad}${insert}${text.slice(b)}`
  return { text: next, caret: a + pad.length + insert.length }
}

// B3c — "Insert ▾": one menu per notice (was 7 buttons under each). Closes on
// a pick, Esc or a press outside; the detail goes in at the textarea's caret.
export const InsertMenu: React.FC<{ label: string; placeholders: { key: string; label: string }[]; onPick: (label: string) => void }> = ({ label, placeholders, onPick }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent): void => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true) }
  }, [open])
  return (
    <div ref={ref} className="relative">
      <button type="button" className={S_BTN_GHOST_SM} aria-haspopup="menu" aria-expanded={open} aria-label={`Insert a detail into ${label}`}
        // keep the textarea's caret: don't take focus from it on press
        onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((v) => !v)}>
        Insert <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div role="menu" aria-label={`Details to insert into ${label}`} className={`${MENU_SURFACE} !w-[260px] absolute right-0 top-[34px] z-20`}>
          {placeholders.map((p) => <MenuItem key={p.key} label={p.label} onClick={() => { setOpen(false); onPick(p.label) }} />)}
        </div>
      )}
    </div>
  )
}

const BillWordingPanel: React.FC<{ onOpenBilling?: () => void }> = ({ onOpenBilling }) => {
  const [data, setData] = useState<BillWordingSettings | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [form, setForm] = useState<Record<TextKey | NoticeKey, string>>({} as Record<TextKey | NoticeKey, string>)
  const [structure, setStructure] = useState<LegalStructure | ''>('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [confirmWithdraw, setConfirmWithdraw] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErr, setFieldErr] = useState<Partial<Record<string, string>>>({})
  const areas = useRef<Partial<Record<NoticeKey, HTMLTextAreaElement | null>>>({})
  // (Big-audit fix batch 1) The Firm names card: read first, the pencil edits the legal name.
  const [namesOpen, setNamesOpen] = useState(false)
  const [legalDraft, setLegalDraft] = useState('')
  const [namesSaving, setNamesSaving] = useState(false)
  const [namesSaved, setNamesSaved] = useState(false)
  const [namesError, setNamesError] = useState<string | null>(null)
  // (Big-audit fix batch 2) …and the trading name.
  const [tradingDraft, setTradingDraft] = useState('')
  const [tradingErr, setTradingErr] = useState<string | null>(null)
  // Another admin changed the firm's settings (e.g. a name) → re-read; while
  // something is being edited here, only the names (the edits are kept).
  const editingRef = useRef(false)
  useFirmChange(['settings'], () => {
    getBillWordingApi().then((d) => {
      if (!editingRef.current) seed(d)
      else setData((prev) => (prev ? { ...prev, firmName: d.firmName } : d))
    }).catch(() => { /* keep what's shown */ })
  })

  const seed = (d: BillWordingSettings): void => {
    setData(d)
    const f = {} as Record<TextKey | NoticeKey, string>
    for (const { key } of [...DETAILS, ...COMPLAINTS]) f[key] = d[key] ?? ''
    for (const { key } of NOTICES) f[key] = wordingToDisplay(d[key] ?? d.defaults[key], d.placeholders)
    setForm(f)
    setStructure(d.legalStructure ?? '')
  }
  useEffect(() => {
    getBillWordingApi().then(seed).catch((e) => setLoadError(serverErrorMessage(e) ?? 'Couldn’t load the bill wording.'))
  }, [])
  if (loadError) return <ErrorNote>{loadError}</ErrorNote>
  if (!data) return <p className={S_NOTE}>Loading…</p>

  // What Save sends: changed values only. A notice equal to Casevo's default → null.
  const patch: BillWordingPatch = {}
  for (const { key } of [...DETAILS, ...COMPLAINTS]) if ((form[key] ?? '').trim() !== (data[key] ?? '')) (patch as Record<string, string | null>)[key] = form[key].trim() || null
  for (const { key } of NOTICES) {
    const v = wordingToTokens((form[key] ?? '').trim(), data.placeholders)
    const next = v === data.defaults[key].trim() ? null : v
    if (next !== data[key]) (patch as Record<string, string | null>)[key] = next
  }
  if ((structure || null) !== (data.legalStructure ?? null)) patch.legalStructure = structure || null
  const dirty = Object.keys(patch).length > 0
  const registrationRequired = structure !== 'PARTNERSHIP' && structure !== 'SOLE_PRACTITIONER'

  const save = async (): Promise<void> => {
    setSaving(true); setError(null); setFieldErr({}); setSaved(false)
    try {
      seed(await patchBillWordingApi(patch))
      setSaved(true)
    } catch (e) {
      const d = (e as { response?: { data?: { field?: string; error?: string } } }).response?.data
      if (d?.field) setFieldErr({ [d.field]: d.error ?? 'Check this.' })
      else setError(serverErrorMessage(e) ?? 'Couldn’t save. Nothing was changed.')
    } finally { setSaving(false) }
  }
  const approve = async (approveIt: boolean): Promise<void> => {
    setBusy(true); setError(null)
    try { seed(await (approveIt ? approveBillWordingApi(data.previewHash) : unapproveBillWordingApi())) } catch (e) {
      setError(serverErrorMessage(e) ?? 'Couldn’t change the approval.')
      // (Audit fix 1) The wording changed while it was being reviewed → show
      // the current text so it can be checked again.
      const fresh = (e as { response?: { status?: number; data?: { data?: BillWordingSettings } } })?.response
      if (fresh?.status === 409 && fresh.data?.data) seed(fresh.data.data)
    } finally { setBusy(false) }
  }
  const insert = (key: NoticeKey, label: string): void => {
    const el = areas.current[key]
    const r = insertAt(form[key] ?? '', `[${label}]`, el ? el.selectionStart : null, el ? el.selectionEnd : null)
    setForm((f) => ({ ...f, [key]: r.text })); setSaved(false)
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(r.caret, r.caret) } })
  }

  // ── Firm names (read first; the pencil edits the legal name) ──
  const legalNow = (data.legalName ?? '').trim()
  const tradingNow = (data.firmName ?? '').trim()
  const tradingNext = tradingDraft.replace(/\s+/g, ' ').trim()
  const tradingChanged = namesOpen && tradingNext !== tradingNow
  const namesDirty = namesOpen && (legalDraft.trim() !== legalNow || tradingChanged)
  editingRef.current = dirty || namesOpen
  const backToNamesPencil = (): void => { window.setTimeout(() => document.querySelector<HTMLElement>('#bw-names [data-edit-pencil]')?.focus(), 0) }
  const openNames = (): void => {
    setLegalDraft(legalNow); setTradingDraft(tradingNow); setTradingErr(null); setNamesError(null); setNamesSaved(false); setNamesOpen(true)
    window.setTimeout(() => document.getElementById('bw-firmName')?.focus(), 0)
  }
  const closeNames = (): void => { setNamesOpen(false); setNamesError(null); setTradingErr(null); backToNamesPencil() }
  const saveNames = async (): Promise<void> => {
    if (!namesDirty) { closeNames(); return }
    const bad = tradingChanged ? tradingNameError(tradingDraft) : null
    setTradingErr(bad)
    if (bad) { window.setTimeout(() => document.getElementById('bw-firmName')?.focus(), 0); return }
    setNamesSaving(true); setNamesError(null)
    let step: 'trading' | 'legal' = 'trading'
    try {
      if (tradingChanged) {
        const r = await patchFirmNameApi(tradingNext)
        setData((prev) => (prev ? { ...prev, firmName: r.firmName } : prev))
        setTradingDraft(r.firmName)
        // This user's app shell (Settings, Admin) shows the new name at once.
        const st = useCasevoStore.getState()
        if (st.authUser) st.setAuthUser({ ...st.authUser, firmName: r.firmName })
      }
      step = 'legal'
      // Only the legal name; the rest of the page keeps whatever is being typed.
      if (legalDraft.trim() !== legalNow) setData(await patchBillWordingApi({ legalName: legalDraft.trim() || null }))
      setNamesOpen(false); setNamesSaved(true); backToNamesPencil()
    } catch (e) {
      const d = (e as { response?: { data?: { field?: string; error?: string } } }).response?.data
      if (step === 'trading' && d?.field === 'name') setTradingErr(d.error ?? 'Check the trading name.')
      else setNamesError(serverErrorMessage(e) ?? (step === 'trading' ? 'Couldn’t save the trading name. Nothing was changed.' : 'Couldn’t save the legal name. The trading name was saved.'))
    } finally { setNamesSaving(false) }
  }
  const differ = namesDiffer(data.firmName, data.legalName)
  const namesCard = (
    <SettingsCard id="bw-names" icon="building" title="Firm names" sub="Your documents can show two names. This is where each one is printed."
      editing={namesOpen}
      right={namesOpen ? <span className="text-[12.5px] text-casevo-text-soft">Editing</span> : <>{namesSaved && <SettingsSaved />}<EditPencil title="firm names" onClick={openNames} /></>}
      footer={namesOpen ? <EditFooter blocked={false} saving={namesSaving} error={namesError} onCancel={closeNames} onSave={() => { void saveNames() }} /> : null}>
      <div data-firm-names="">
        <SettingsRow label="Trading name">
          {namesOpen ? (
            <div className="font-normal">
              <label htmlFor="bw-firmName" className="sr-only">Trading name</label>
              <input id="bw-firmName" type="text" value={tradingDraft} required placeholder="e.g. Smith & Co"
                aria-invalid={tradingErr ? true : undefined} aria-describedby={`${tradingErr ? 'bw-firmName-err ' : ''}bw-firmName-where`}
                onChange={(e) => { setTradingDraft(e.target.value); if (tradingErr) setTradingErr(null) }} className={sInput(!!tradingErr)} />
              <div id="bw-firmName-err">{tradingErr && <FieldError message={tradingErr} />}</div>
              <p id="bw-firmName-where" className={`${S_NOTE} mt-[5px] mb-0`} data-trading-change-note="">{TRADING_NAME_WHERE} {TRADING_NAME_CHANGE_NOTE}</p>
            </div>
          ) : (
            <>
              <span data-trading-name="">{data.firmName || 'Not set'}</span>
              <RowSmall>{TRADING_NAME_WHERE}</RowSmall>
            </>
          )}
        </SettingsRow>
        <SettingsRow label="Legal name">
          {namesOpen ? (
            <div className="font-normal">
              <label htmlFor="bw-legalName" className="sr-only">Legal name</label>
              <input id="bw-legalName" type="text" value={legalDraft} placeholder="e.g. Smith & Co Solicitors LLP"
                aria-describedby="bw-legalName-where" onChange={(e) => setLegalDraft(e.target.value)} className={sInput(false)} />
              <p id="bw-legalName-where" className={`${S_NOTE} mt-[5px] mb-0`}>{LEGAL_NAME_WHERE}{data.approved ? ' Saving makes the wording DRAFT again until it’s re-approved.' : ''}</p>
            </div>
          ) : (
            <>
              {legalNow ? <span data-legal-name="">{legalNow}</span> : <span className="text-casevo-text-soft font-normal" data-legal-name="">Not set</span>}
              <RowSmall>{LEGAL_NAME_WHERE}</RowSmall>
            </>
          )}
        </SettingsRow>
        {differ && !namesOpen && (
          <ActionBox tone="info" className="mt-[10px]" title="The two names differ"
            message={`Documents show “${data.firmName}” at the top and “${legalNow}” in the footer and signature line. That’s normal if your firm trades under a different name from its legal entity — check both are right.`} />
        )}
      </div>
    </SettingsCard>
  )

  const status = (
    // (Audit fix batch 2) Green = approved; red = not approved (a problem: nothing can go out).
    <ActionBox live tone={data.approved ? 'done' : 'danger'} title={wordingStatusText(data)} message={data.approved ? undefined : 'Bills can be drafted and previewed, but can’t be issued or sent to a client until a solicitor at your firm approves this wording.'} />
  )

  const textField = ({ key, label, placeholder, type }: { key: TextKey; label: string; placeholder: string; type?: string }): React.ReactNode => {
    const hint = key === 'companyRegistration' ? (registrationRequired ? COMPANY_REGISTRATION_HINT : `${COMPANY_REGISTRATION_HINT}. Not needed for a partnership or sole practitioner.`) : null
    const describedBy = [fieldErr[key] ? `bw-${key}-err` : '', hint ? `bw-${key}-hint` : ''].filter(Boolean).join(' ') || undefined
    return (
      <div key={key}>
        <label htmlFor={`bw-${key}`} className={S_LABEL}>{label}{key === 'companyRegistration' && !registrationRequired ? ' (optional)' : ''}</label>
        <input id={`bw-${key}`} type={type ?? 'text'} value={form[key] ?? ''} placeholder={placeholder}
          aria-invalid={fieldErr[key] ? true : undefined} aria-describedby={describedBy}
          onChange={(e) => { setForm((f) => ({ ...f, [key]: e.target.value })); setSaved(false) }} className={sInput(!!fieldErr[key])} />
        {fieldErr[key] && <p id={`bw-${key}-err`} className={S_FIELD_ERROR}>{fieldErr[key]}</p>}
        {hint && <p id={`bw-${key}-hint`} className={`${S_NOTE} mt-[5px] mb-0`}>{hint}</p>}
      </div>
    )
  }
  const complaintsLabel = data.placeholders.find((p) => p.key === 'complaintsContact')?.label ?? 'Complaints contact'

  return (
    <div className="space-y-[18px]">
      <SettingsCard icon="file" title="Bill wording" sub="The legal wording printed on every bill. All Casevo default text is DRAFT and must be reviewed by a solicitor at your firm.">
        {status}
        {data.problems.length > 0 && (
          <div className="mt-[12px] text-[12.5px] text-casevo-forest">
            <b className="font-semibold">To fill in before it can be approved:</b>
            <ul className="mt-[4px] mb-0 pl-[18px] list-disc space-y-[2px]">{data.problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}
        <div className={S_FORMFOOT}>
          {data.approved
            ? <button type="button" className={S_BTN_GHOST} disabled={busy} onClick={() => setConfirmWithdraw(true)}>Withdraw approval</button>
            : <button type="button" className={S_BTN_PRIMARY} disabled={busy || dirty || data.problems.length > 0} onClick={() => { void approve(true) }}
                title={dirty ? 'Save your changes first' : data.problems.length ? 'Fill in what’s missing first' : undefined}>Approve wording</button>}
          {!data.approved && dirty && <span className={S_NOTE}>Save your changes before approving.</span>}
        </div>
      </SettingsCard>

      {namesCard}

      <SettingsCard icon="users" title="Firm legal details" sub="Filled into the SRA footer and the signature line. The legal name is under Firm names, above.">
        <div className={S_FIELDS}>
          <div>
            <label htmlFor="bw-legalStructure" className={S_LABEL}>Legal structure</label>
            <select id="bw-legalStructure" value={structure} onChange={(e) => { setStructure(e.target.value as LegalStructure | ''); setSaved(false) }}
              aria-invalid={fieldErr.legalStructure ? true : undefined} aria-describedby={fieldErr.legalStructure ? 'bw-legalStructure-err' : 'bw-legalStructure-hint'} className={sInput(!!fieldErr.legalStructure)}>
              <option value="">Not set</option>
              {(data.legalStructures ?? []).map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            {fieldErr.legalStructure
              ? <p id="bw-legalStructure-err" className={S_FIELD_ERROR}>{fieldErr.legalStructure}</p>
              : <p id="bw-legalStructure-hint" className={`${S_NOTE} mt-[5px] mb-0`}>A limited company or LLP needs its company registration on bills; a partnership or sole practitioner doesn’t.</p>}
          </div>
          {DETAILS.map(textField)}
          <div>
            <span className={S_LABEL}>SRA and VAT numbers</span>
            <p className={`${S_NOTE} m-0`}>SRA {data.sraNumber || 'not set'} · VAT {data.vatNumber || 'not set'}. {onOpenBilling ? <button type="button" className="underline" onClick={onOpenBilling}>Edit in Billing</button> : 'Edit them in Admin › Billing.'}</p>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard icon="mail" title="Complaints contact" sub={`Used wherever the wording shows [${complaintsLabel}]. Left empty, the older free-text contact is used${data.complaintsContact ? ` (“${data.complaintsContact}”)` : ''}.`}>
        <div className={S_FIELDS}>{COMPLAINTS.map(textField)}</div>
      </SettingsCard>

      <SettingsCard icon="pen" title="Notices" sub="Your details are filled in wherever you insert them. The Legal Ombudsman details are set by Casevo.">
        {NOTICES.map(({ key, label, sub, preview }) => (
          <div key={key} className="mt-[16px]">
            <div className="flex items-center gap-[8px] flex-wrap">
              <label htmlFor={`bw-${key}`} className={`${S_LABEL} mb-0`}>{label}</label>
              <span className={S_NOTE}>{sub}</span>
              <div className="ml-auto flex items-center gap-[6px]">
                <InsertMenu label={label} placeholders={data.placeholders} onPick={(l) => insert(key, l)} />
                {wordingToTokens((form[key] ?? '').trim(), data.placeholders) !== data.defaults[key].trim() && (
                  <button type="button" className={S_BTN_GHOST_SM} onClick={() => { setForm((f) => ({ ...f, [key]: wordingToDisplay(data.defaults[key], data.placeholders) })); setSaved(false) }}>Use Casevo’s default</button>
                )}
              </div>
            </div>
            <textarea id={`bw-${key}`} ref={(el) => { areas.current[key] = el }} rows={key === 'billSraFooter' ? 2 : 6} value={form[key] ?? ''}
              aria-invalid={fieldErr[key] ? true : undefined} aria-describedby={fieldErr[key] ? `bw-${key}-err` : `bw-${key}-preview`}
              onChange={(e) => { setForm((f) => ({ ...f, [key]: e.target.value })); setSaved(false) }}
              className={`${sInput(!!fieldErr[key])} !h-auto py-[8px] mt-[6px] resize-y leading-[1.5]`} />
            {fieldErr[key] && <p id={`bw-${key}-err`} className={S_FIELD_ERROR}>{fieldErr[key]}</p>}
            <p id={`bw-${key}-preview`} className={`${S_NOTE} mt-[6px]`}><b className="font-semibold text-casevo-forest">As printed (saved version):</b> {data.preview[preview]}</p>
          </div>
        ))}
        {error && <ErrorNote className="mt-[14px]">{error}</ErrorNote>}
        <div className={S_FORMFOOT}>
          <button type="button" className={S_BTN_PRIMARY} disabled={!dirty || saving} onClick={() => { void save() }}>{saving ? 'Saving…' : 'Save wording'}</button>
          {dirty && data.approved && <span className={S_NOTE}>Saving makes the wording DRAFT again until it’s re-approved.</span>}
          {saved && !dirty && <SettingsSaved />}
          <UnsavedGuard dirty={(dirty && !saving) || (namesDirty && !namesSaving)} />
        </div>
      </SettingsCard>
      <ConfirmDialogue isOpen={confirmWithdraw} title="Withdraw approval of the bill wording?"
        message="The wording goes back to DRAFT. Bills can’t be issued or sent to a client until a solicitor at your firm approves it again."
        confirmLabel="Withdraw approval" tone="danger" onCancel={() => setConfirmWithdraw(false)} onConfirm={() => { setConfirmWithdraw(false); void approve(false) }} />
    </div>
  )
}

export default BillWordingPanel
